export type MtlDefault = string | number | { Value: string; Unit: string };

export interface MtlParameter {
  Name: string;
  Type: string;
  Default: MtlDefault;
  Access: string;
}

export interface MtlMaterial {
  Name: string;
  PARAMETERS: MtlParameter[];
}

export type MtlFile = Record<string, MtlMaterial>;

// ----- Tokenizer -----

type TokenKind =
  | 'IDENT'
  | 'EQ'
  | 'LBRACE'
  | 'RBRACE'
  | 'COMMA'
  | 'QUOTED_STRING'
  | 'NUMBER'
  | 'EOF';

interface Token {
  kind: TokenKind;
  value: string;
  unit?: string;
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let pos = 0;
  const len = source.length;

  while (pos < len) {
    const ch = source[pos];

    if (ch === ' ' || ch === '\t' || ch === '\r' || ch === '\n') {
      pos++;
      continue;
    }

    if (ch === '{') { tokens.push({ kind: 'LBRACE', value: '{' }); pos++; continue; }
    if (ch === '}') { tokens.push({ kind: 'RBRACE', value: '}' }); pos++; continue; }
    if (ch === '=') { tokens.push({ kind: 'EQ',     value: '=' }); pos++; continue; }
    if (ch === ',') { tokens.push({ kind: 'COMMA',  value: ',' }); pos++; continue; }

    if (ch === "'") {
      pos++;
      let str = '';
      while (pos < len && source[pos] !== "'") {
        str += source[pos++];
      }
      if (pos < len) pos++; // consume closing quote
      tokens.push({ kind: 'QUOTED_STRING', value: str });
      continue;
    }

    if (ch >= '0' && ch <= '9') {
      const start = pos;
      const numMatch = source.slice(pos).match(/^(\d+(?:\.\d+)?(?:[eE][+\-]\d+)?)/);
      if (!numMatch) { pos++; continue; }
      pos += numMatch[1].length;

      // If a value starts with digits but immediately continues as an identifier
      // (e.g. 1010_STRIP_SAE5040), it is a string token, not a numeric+unit token.
      const next = source[pos];
      if (next && ((next >= 'a' && next <= 'z') || (next >= 'A' && next <= 'Z') || next === '_' || next === '-' || next === '+' || next === '.')) {
        let ident = source.slice(start, pos);
        while (pos < len) {
          const c = source[pos];
          if (
            (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') ||
            (c >= '0' && c <= '9') || c === '_' || c === '-' || c === '+' || c === '.' || c === '/'
          ) {
            ident += c;
            pos++;
          } else {
            break;
          }
        }
        tokens.push({ kind: 'IDENT', value: ident });
        continue;
      }

      // Capture the rest of the line as a potential unit
      let restOfLine = '';
      while (pos < len && source[pos] !== '\n' && source[pos] !== '\r') {
        restOfLine += source[pos++];
      }

      const unit = restOfLine.trim();
      const token: Token = { kind: 'NUMBER', value: numMatch[1] };
      if (unit) token.unit = unit;
      tokens.push(token);
      continue;
    }

    if ((ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_') {
      let ident = '';
      while (pos < len) {
        const c = source[pos];
        if (
          (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') ||
          (c >= '0' && c <= '9') || c === '_' || c === '-' || c === '+' || c === '.' || c === '/'
        ) {
          ident += c;
          pos++;
        } else {
          break;
        }
      }
      tokens.push({ kind: 'IDENT', value: ident });
      continue;
    }

    pos++; // skip unrecognised character
  }

  tokens.push({ kind: 'EOF', value: '' });
  return tokens;
}

// ----- Recursive-descent parser -----

class MtlParser {
  private readonly tokens: Token[];
  private pos = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  private peek(offset = 0): Token {
    const idx = this.pos + offset;
    return idx < this.tokens.length ? this.tokens[idx] : { kind: 'EOF', value: '' };
  }

  private consume(): Token {
    return this.tokens[this.pos++];
  }

  private expect(kind: TokenKind): Token {
    const t = this.consume();
    if (t.kind !== kind) {
      throw new Error(`MTL parse error: expected '${kind}', got '${t.kind}' ("${t.value}")`);
    }
    return t;
  }

  // Parses the entire file: one or more top-level  Name = { ... }  blocks
  parseFile(): MtlFile {
    const result: MtlFile = {};
    while (this.peek().kind !== 'EOF') {
      const name = this.expect('IDENT').value;
      this.expect('EQ');
      this.expect('LBRACE');
      const body = this.parseBody();
      this.expect('RBRACE');
      result[name] = body as unknown as MtlMaterial;
    }
    return result;
  }

  // Parses key=value assignments until RBRACE or EOF
  private parseBody(): Record<string, unknown> {
    const obj: Record<string, unknown> = {};
    while (this.peek().kind !== 'RBRACE' && this.peek().kind !== 'EOF') {
      const key = this.expect('IDENT').value;
      this.expect('EQ');
      obj[key] = this.parseValue();
    }
    return obj;
  }

  // Parses a single { key=value ... } block and applies post-processing
  private parseBlock(): Record<string, unknown> {
    this.expect('LBRACE');
    const obj = this.parseBody();
    this.expect('RBRACE');

    // Differentiate Integer (→ plain JS number) vs Real (→ { Value, Unit } or plain string)
    const type = obj['Type'];
    const def = obj['Default'];
    if ((type === 'Integer' || type === 'Real') && typeof def === 'object' && def !== null && 'Value' in def) {
      const { Value, Unit } = def as { Value: string; Unit: string };
      if (type === 'Integer') {
        obj['Default'] = parseInt(Value, 10);
      } else {
        obj['Default'] = { Value, Unit: Unit ?? '' };
      }
    }

    return obj;
  }

  // Parses a value on the right-hand side of an assignment.
  // If it starts with '{' and is followed by one or more additional
  // comma-separated '{' blocks, the whole sequence is returned as an array.
  private parseValue(): unknown {
    const t = this.peek();

    if (t.kind === 'QUOTED_STRING') {
      this.consume();
      return t.value;
    }

    if (t.kind === 'NUMBER') {
      this.consume();
      return { Value: t.value, Unit: t.unit || '' };
    }

    if (t.kind === 'IDENT') {
      this.consume();
      return t.value;
    }

    if (t.kind === 'LBRACE') {
      const first = this.parseBlock();

      // If the next tokens are  ,  {  this is a multi-block array
      if (this.peek().kind === 'COMMA' && this.peek(1).kind === 'LBRACE') {
        const items: Record<string, unknown>[] = [first];
        while (this.peek().kind === 'COMMA' && this.peek(1).kind === 'LBRACE') {
          this.consume(); // consume comma
          items.push(this.parseBlock());
        }
        return items;
      }

      return first;
    }

    throw new Error(`MTL parse error: unexpected token '${t.kind}' ("${t.value}")`);
  }
}

// ----- Public API -----

export function parseMtl(source: string): MtlFile {
  const tokens = tokenize(source);
  return new MtlParser(tokens).parseFile();
}
